import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const MaintenanceContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAINTENANCE_POLL_INTERVAL_MS = 30000;

export const MaintenanceProvider = ({ children }) => {
  const [status, setStatus] = useState({
    is_enabled: false,
    updated_at: null
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  const refreshStatus = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setIsLoading(true);
    }

    try {
      const response = await axios.get(`${API_URL}/maintenance`);
      setStatus(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching maintenance status:', error);
      return null;
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      refreshStatus({ silent: true });
    }, MAINTENANCE_POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [refreshStatus]);

  const updateMaintenanceStatus = useCallback(async (isEnabled, token) => {
    setIsUpdating(true);

    try {
      const response = await axios.put(
        `${API_URL}/admin/maintenance`,
        { is_enabled: isEnabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setStatus({
        is_enabled: response.data.is_enabled,
        updated_at: response.data.updated_at
      });

      return response.data;
    } finally {
      setIsUpdating(false);
    }
  }, []);

  const value = useMemo(() => ({
    maintenanceEnabled: status.is_enabled,
    maintenanceUpdatedAt: status.updated_at,
    isLoadingMaintenance: isLoading,
    isUpdatingMaintenance: isUpdating,
    refreshMaintenanceStatus: refreshStatus,
    updateMaintenanceStatus
  }), [status, isLoading, isUpdating, refreshStatus, updateMaintenanceStatus]);

  return (
    <MaintenanceContext.Provider value={value}>
      {children}
    </MaintenanceContext.Provider>
  );
};

export const useMaintenance = () => {
  const context = useContext(MaintenanceContext);

  if (!context) {
    throw new Error('useMaintenance must be used within a MaintenanceProvider');
  }

  return context;
};

export default MaintenanceContext;
